import { Component, DestroyRef, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AddressAPIService, CountryConfiguration } from '@vendasta/address';
import {
  CompositeFilterOperator,
  FieldFilterOperator,
  FieldFilterOperatorFunction,
  Filter,
  PropertyType,
} from '@vendasta/multi-location-analytics';
import { TaxonomyOption } from '@vendasta/taxonomy';
import { asyncScheduler, combineLatest, Observable } from 'rxjs';
import { map, observeOn, switchMap, withLatestFrom } from 'rxjs/operators';
import { LanguageService } from '../../core/language-service.service';
import { isMobile } from '../../core/mobile';
import { AccountGroupMetricService } from '../../metrics/account-group.service';
import { Mode, SidepanelService, Size, SizePixels } from '../../navigation/sidepanel.service';
import { FilterService } from '../../shared/filter-service.service';
import { TimeRangeService } from '../../shared/time-range.service';
import { BrandsService } from '../brands.service';
import {
  BrandFilterContainerService,
  CITIES_LABEL,
  COUNTRIES_LABEL,
  DisplayFilter,
  FilterDisplaySettings,
  Location,
  LocationGroup,
  STATEPROV_LABEL,
} from './brand-filter-container.service';
import { BrandGeographyDialogComponent } from './brand-geography-dialog/brand-geography-dialog.component';
import { BrandGroupDialogComponent, GroupWithDisplayName } from './brand-group-dialog/brand-group-dialog.component';
import { BrandLocationDialogComponent } from './brand-location-dialog/brand-location-dialog.component';
import { BrandSourceDialogComponent } from './brand-source-dialog/brand-source-dialog.component';
import { BrandTaxonomyDialogComponent } from './brand-taxonomy-dialog/brand-taxonomy-dialog.component';

@Component({
  selector: 'bc-brand-filter-container',
  templateUrl: './brand-filter-container.component.html',
  styleUrls: ['./brand-filter-container.component.scss'],
  standalone: false,
})
export class BrandFilterContainerComponent implements OnInit {
  filterItems: DisplayFilter[] = [];
  filterSettings: FilterDisplaySettings = {
    textColor: '',
    backgroundColor: '',
  };
  filterQuantity$: Observable<number>;

  availableLocations: LocationGroup[];
  selectedCountries: Location[] = [];
  selectedStateProvinces: Location[] = [];
  selectedCities: Location[] = [];

  selectedGroup: GroupWithDisplayName;

  selectedTaxonomies: TaxonomyOption[] = [];

  filterBarOffset$: Observable<string>;

  sidenavOpened$ = this.sidepanelService.visible$;
  dateText$: Observable<string>;

  geographyFilter$: Observable<boolean>;
  busCategoryFilter$: Observable<boolean>;
  groupFilter$: Observable<boolean>;
  locationFilter$: Observable<boolean>;
  sourceFilter$: Observable<boolean>;

  displaySourceFilter = input<boolean>(true);

  constructor(
    private dialog: MatDialog,
    private accountGroupMetricService: AccountGroupMetricService,
    private filterService: FilterService,
    private timeRangeService: TimeRangeService,
    private brandsService: BrandsService,
    private sidepanelService: SidepanelService,
    public brandFiltersService: BrandFilterContainerService,
    private languageService: LanguageService,
    private addressService: AddressAPIService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.brandFiltersService.openDialog$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (dialog) => {
        switch (dialog) {
          case 'location':
            this.openLocationDialog();
        }
      },
    });

    // Add Location filter which displays only if locations are selected
    this.addOrUpdateFilterItem(
      'location-filter',
      'COMMON.LOCATION',
      null,
      this.brandFiltersService.locationFilterDisplay$,
    );
    // But still watch the selection to update the filters..
    this.brandFiltersService.selectedAccountGroupIds$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.calculateFilters();
    });

    // Add Source filter which displays only if sources are selected
    this.addOrUpdateFilterItem(
      'source-filter',
      'COMMON.REVIEW_OR_LISTING_SOURCE',
      null,
      this.brandFiltersService.sourceFilterDisplay$,
    );

    const geographicBreakdown$ = this.accountGroupMetricService.getGeographicBreakdown$();
    combineLatest([this.languageService.currentLocale$, geographicBreakdown$])
      .pipe(
        switchMap(([currentLocale, geographicBreakdown]) => {
          const countryCodes = geographicBreakdown.reduce((codes, location) => {
            if (location.countryCode && codes.indexOf(location.countryCode) === -1) {
              codes.push(location.countryCode);
            }
            return codes;
          }, []);
          return combineLatest(
            countryCodes.map((code) => this.addressService.getCountryConfiguration(code, currentLocale)),
          );
        }),
        map((countryConfigs: CountryConfiguration[]) => {
          return countryConfigs.reduce(
            (options: Record<string, CountryConfiguration>, country: CountryConfiguration) => {
              options[country.code] = country;
              return options;
            },
            {},
          );
        }),
        withLatestFrom(geographicBreakdown$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([countryOptions, geographicBreakdown]) => {
        if (typeof geographicBreakdown === 'undefined' || geographicBreakdown === null) {
          return;
        }
        this.clearAvailableLocations();
        geographicBreakdown.forEach((location) => {
          if (location.country && !location.state) {
            this.availableLocations[0].options.push({
              id: location.countryCode,
              name: countryOptions[location.countryCode].name,
              parent: [],
              locationCount: location.numLocations,
            });
          }
          if (location.country && location.state && !location.city) {
            const state = countryOptions[location.countryCode].zones?.find((zone) => {
              return zone.code === location.stateCode;
            });
            this.availableLocations[1].options.push({
              id: location.stateCode,
              name: state?.name,
              parent: [location.countryCode],
              locationCount: location.numLocations,
            });
          }
          if (location.country && location.state && location.city) {
            this.availableLocations[2].options.push({
              id: location.city,
              name: `${location.city}, ${location.stateCode}`,
              parent: [location.countryCode, location.stateCode],
              locationCount: location.numLocations,
            });
          }
        });
        this.availableLocations[0].options.sort(this.sortName);
        this.availableLocations[1].options.sort(this.sortName);
        this.availableLocations[2].options.sort(this.sortName);
      });

    this.filterBarOffset$ = combineLatest([
      this.sidepanelService.mode$,
      this.sidepanelService.size$,
      this.sidepanelService.visible$,
    ]).pipe(
      observeOn(asyncScheduler),
      map(([mode, size, visible]) => {
        if (mode !== Mode.SIDE || isMobile()) {
          return '0';
        }
        if (!visible) {
          return '0';
        }
        if (size === Size.SMALL) {
          return `${SizePixels.SMALL}px`;
        } else if (size === Size.REGULAR) {
          return `${SizePixels.REGULAR}px`;
        } else {
          return `${SizePixels.LARGE}px`;
        }
      }),
    );

    this.dateText$ = combineLatest([
      this.brandFiltersService.dateFilterDisplay$,
      this.timeRangeService.dateRange$,
      this.languageService.currentLocale$,
    ]).pipe(
      map(([type, [start, end], locale]) => {
        if (type === 'Custom' && start && end) {
          return start.toLocaleDateString(locale) + ' - ' + end.toLocaleDateString(locale);
        } else {
          return type;
        }
      }),
    );
    this.filterQuantity$ = this.filterService.filtersQuantity$;
    this.groupFilter$ = this.filterService.subgroup$.pipe(map((r) => r != null));
    this.locationFilter$ = this.brandFiltersService.locationFilterDisplay$.pipe(map((r) => r.length > 0));
    this.sourceFilter$ = this.filterService.filteredSources$.pipe(map((r) => r.length > 0));
    this.geographyFilter$ = this.filterQuantity$.pipe(
      map(() => this.selectedCities.concat(this.selectedStateProvinces).concat(this.selectedCountries).length > 0),
    );
    this.busCategoryFilter$ = this.filterQuantity$.pipe(map(() => this.selectedTaxonomies.length > 0));
  }

  private clearAvailableLocations(): void {
    this.availableLocations = [
      {
        name: COUNTRIES_LABEL,
        options: [],
      },
      {
        name: STATEPROV_LABEL,
        options: [],
      },
      {
        name: CITIES_LABEL,
        options: [],
      },
    ];
  }

  private sortName(a: Location, b: Location): number {
    return a.name?.localeCompare(b.name);
  }

  addOrUpdateFilterItem(
    filterId: string,
    filterTypeTranslateKey: string,
    value: string,
    value$?: Observable<string>,
  ): void {
    const foundFilter = this.filterItems.filter((item) => item.filterId === filterId);
    if (foundFilter.length > 0) {
      foundFilter[0].displayName = value;
      this.filterItems[this.filterItems.findIndex((el) => el.filterId === filterId)] = foundFilter[0];
      return;
    }
    this.filterItems.push({
      filterId: filterId,
      filterTypeTranslateKey: filterTypeTranslateKey,
      displayName: value,
      displayName$: value$,
    });
  }

  removeFilterItem(filterId: string): void {
    if (filterId === 'location-filter') {
      // This will no longer display the chip and recalculate the filters
      this.brandFiltersService.clearAccountGroupIds();
      return;
    } else if (filterId === 'source-filter') {
      // This will no longer display the chip and recalculate the filters
      this.brandFiltersService.clearSourceIds();
      return;
    }
    this.filterItems = this.filterItems.filter((filter) => filter.filterId !== filterId);
    if (filterId === 'geography-filter') {
      this.selectedCountries = [];
      this.selectedStateProvinces = [];
      this.selectedCities = [];
      this.calculateFilters();
    } else if (filterId === 'group-filter') {
      this.brandsService.clearPath();
      this.selectedGroup = null;
      this.filterService.setSubgroup(null);
    } else if (filterId === 'category-filter') {
      this.selectedTaxonomies = [];
      this.calculateFilters();
    }
  }

  selectFilterItem(filterId: string): void {
    if (filterId === 'geography-filter') {
      this.openGeographyDialog();
    } else if (filterId === 'group-filter') {
      this.openGroupDialog();
    } else if (filterId === 'category-filter') {
      this.openTaxonomyDialog();
    } else if (filterId === 'location-filter') {
      this.openLocationDialog();
    } else if (filterId === 'source-filter') {
      this.openSourceDialog();
    }
  }

  private calculateGeographyFilter(): string {
    if (this.selectedCountries.length + this.selectedStateProvinces.length + this.selectedCities.length > 1) {
      let displayValue = '';
      if (this.selectedCountries.length === 1) {
        displayValue += '1 Country';
      } else if (this.selectedCountries.length > 1) {
        displayValue += `${this.selectedCountries.length} Countries`;
      }

      if (this.selectedCountries.length > 0 && this.selectedStateProvinces.length > 0) {
        displayValue += this.selectedCities.length > 0 ? ', ' : ' and ';
      }

      if (this.selectedStateProvinces.length === 1) {
        displayValue += '1 State/Province';
      } else if (this.selectedStateProvinces.length > 1) {
        displayValue += `${this.selectedStateProvinces.length} States/Provinces`;
      }

      if (
        (this.selectedCountries.length > 0 || this.selectedStateProvinces.length > 0) &&
        this.selectedCities.length > 0
      ) {
        displayValue += ' and ';
      }

      if (this.selectedCities.length === 1) {
        displayValue += '1 City';
      } else if (this.selectedCities.length > 1) {
        displayValue += `${this.selectedCities.length} Cities`;
      }
      return displayValue;
    }
    if (this.selectedCountries.length > 0) {
      return this.selectedCountries[0].id;
    }
    if (this.selectedStateProvinces.length > 0) {
      return this.selectedStateProvinces[0].id;
    }
    if (this.selectedCities.length > 0) {
      return this.selectedCities[0].id;
    }
  }

  private calculateGroupFilter(): string {
    if (this.selectedGroup) {
      return this.selectedGroup.name;
    }
  }

  private calculateTaxonomyFilter(): string {
    // Take the last part of the tax ids and join them
    return '' + this.selectedTaxonomies.map((taxOpt) => taxOpt.name.split(' > ').pop()).join(', ');
  }

  private calculatePartnerColor(theme: string, primaryColor?: string): void {
    let selectedColor = this.filterSettings.textColor;
    if (theme === 'dark' && primaryColor) {
      selectedColor = '#FFFFFF';
    }
    this.filterSettings = {
      textColor: selectedColor,
      backgroundColor: primaryColor ? `#${primaryColor}` : '#EAEAEA',
    };
  }

  openGeographyDialog(): void {
    const dialogRef = this.dialog.open(BrandGeographyDialogComponent, {
      data: {
        availableLocations: this.availableLocations,
        selectedCountries: this.selectedCountries,
        selectedStateProvinces: this.selectedStateProvinces,
        selectedCities: this.selectedCities,
        filterSettings: this.filterSettings,
      },
      id: 'brand-geography-container',
    });
    dialogRef.afterClosed().subscribe((results) => {
      if (typeof results !== 'undefined') {
        this.selectedCountries = results.selectedCountries;
        this.selectedStateProvinces = results.selectedStateProvinces;
        this.selectedCities = results.selectedCities;

        // Empty selection should clear filter chip
        if (
          this.selectedCountries.length === 0 &&
          this.selectedStateProvinces.length === 0 &&
          this.selectedCities.length === 0
        ) {
          this.removeFilterItem('geography-filter');
          this.calculateFilters();
          return;
        }

        this.addOrUpdateFilterItem(
          'geography-filter',
          'PERFORMANCE.MULTI_LOCATION.FILTERS.TITLES.GEOGRAPHY',
          this.calculateGeographyFilter(),
        );
        this.calculateFilters();
      }
    });
  }

  openGroupDialog(): void {
    const dialogRef = this.dialog.open(BrandGroupDialogComponent, {
      data: {
        selectedGroup: this.selectedGroup,
        filterSettings: this.filterSettings,
      },
      id: 'brand-group-container',
    });
    dialogRef.afterClosed().subscribe((results) => {
      if (typeof results !== 'undefined') {
        this.selectedGroup = <GroupWithDisplayName>results.selectedGroup;
        this.filterService.setSubgroup(this.selectedGroup.group.pathNodes);

        this.addOrUpdateFilterItem(
          'group-filter',
          'PERFORMANCE.MULTI_LOCATION.FILTERS.TITLES.GROUP',
          this.calculateGroupFilter(),
        );

        this.brandsService.setPath(this.selectedGroup.group.pathNodes);
      } else {
        this.selectedGroup = null;
        this.filterService.setSubgroup(null);
        this.brandsService.clearPath();
      }
    });
  }

  openTaxonomyDialog(): void {
    const dialogRef = this.dialog.open(BrandTaxonomyDialogComponent, {
      data: {
        selectedTaxonomies: this.selectedTaxonomies,
        filterSettings: this.filterSettings,
      },
      id: 'brand-taxonomies-container',
    });
    dialogRef.afterClosed().subscribe((results) => {
      if (typeof results !== 'undefined') {
        this.selectedTaxonomies = results.selectedTaxonomies as TaxonomyOption[];

        // Empty selection should clear filter chip
        if (this.selectedTaxonomies.length === 0) {
          this.removeFilterItem('category-filter');
          this.calculateFilters();
          return;
        }

        this.addOrUpdateFilterItem(
          'category-filter',
          'PERFORMANCE.MULTI_LOCATION.FILTERS.TITLES.CATEGORY',
          this.calculateTaxonomyFilter(),
        );
        this.calculateFilters();
      }
    });
  }

  openLocationDialog(): void {
    const dialogRef = this.dialog.open(BrandLocationDialogComponent, {
      data: {
        filterSettings: this.filterSettings,
      },
      id: 'brand-location-filter-container',
      panelClass: ['mobile-full-width'],
    });
    dialogRef.afterClosed().subscribe((results) => {
      if (typeof results !== 'undefined') {
        // Setting this updates the field, clears the chip if nothing, and recaclculates filters
        this.brandFiltersService.setAccountGroupIds(results.selectedAccountGroupIds);
      }
    });
  }

  openSourceDialog(): void {
    const dialogRef = this.dialog.open(BrandSourceDialogComponent, {
      data: {
        selectedSourceIds: this.brandFiltersService.getSourceIds(),
        filterSettings: this.filterSettings,
      },
      id: 'brand-source-filter-container',
    });
    dialogRef.afterClosed().subscribe((results) => {
      if (typeof results !== 'undefined') {
        // Setting this updates the field, clears the chip if nothing, and recaclculates filters
        this.brandFiltersService.setSourceIds(results.selectedSourceIds);
      }
      this.calculateFilters();
    });
  }

  private calculateGeographyFilters(): Filter {
    if (
      this.selectedCountries.length === 0 &&
      this.selectedStateProvinces.length === 0 &&
      this.selectedCities.length === 0
    ) {
      return null;
    }
    const filterObject = new Filter({
      compositeFilter: {
        op: CompositeFilterOperator.OR,
        filters: [],
      },
    });
    [...this.selectedCountries, ...this.selectedStateProvinces, ...this.selectedCities].forEach((location) => {
      let dimension = 'country';
      if (location.parent.length === 1) {
        dimension = 'state';
      } else if (location.parent.length === 2) {
        dimension = 'city';
      }
      filterObject.compositeFilter.filters.push(
        new Filter({
          fieldFilter: {
            operator: FieldFilterOperator.EQUAL,
            dimension: `account_group__${dimension}`,
            value: {
              value: location.id,
              valueType: PropertyType.PROPERTY_TYPE_STRING,
            },
          },
        }),
      );
    });

    return filterObject;
  }

  private calculateTaxonomyFilters(): Filter {
    if (this.selectedTaxonomies.length === 0) {
      return null;
    }
    return new Filter({
      fieldFilter: {
        operator: FieldFilterOperator.OVERLAP,
        dimension: `account_group__tax_ids`,
        value: {
          value: this.selectedTaxonomies.map((taxOpt) => taxOpt.id),
          valueType: PropertyType.PROPERTY_TYPE_STRING,
        },
      },
    });
  }

  private calculateLocationFilters(): Filter {
    if (this.brandFiltersService.getAccountGroupIds().length === 0) {
      return null;
    }
    return new Filter({
      fieldFilter: {
        operator: FieldFilterOperator.EQUAL,
        operatorFunction: FieldFilterOperatorFunction.ANY,
        // TODO: Minor(?) opportunity here to not require the join for this if all metrics had same agid field
        dimension: `account_group__account_group_id`,
        value: {
          value: this.brandFiltersService.getAccountGroupIds(),
          valueType: PropertyType.PROPERTY_TYPE_STRING,
        },
      },
    });
  }

  private calculateFilters(): void {
    const geoFilter = this.calculateGeographyFilters();
    const taxonomyFilter = this.calculateTaxonomyFilters();
    const locationFilter = this.calculateLocationFilters();
    this.filterService.setFilters([geoFilter, taxonomyFilter, locationFilter].filter((x) => !!x));
  }

  public clearAllFilters(): void {
    this.removeFilterItem('location-filter');
    this.removeFilterItem('source-filter');
    this.removeFilterItem('geography-filter');
    this.removeFilterItem('group-filter');
    this.removeFilterItem('category-filter');
  }
}
